import { z } from 'zod';

import {
	createLog,
	LogSeverity,
	PROGRAM_SECTIONS,
	type ProgramOwnerCheckoutFulfillmentDto,
	type ProgramOwnerServiceConfig,
	Services,
	type ServicesProvider,
	SUBSECTIONS,
} from '@ekhodealer/ekho-common';
import { Box, Form, Stack } from '@ekhodealer/ekho-common/rev';

import { SettingsSection } from '@common/components/SettingsSection';
import { useCurrentServicesProvider } from '@common/hooks/useCurrentServicesProvider';

import { useUpdateCheckoutFulfillment } from '../../../../api/configuration/services/useUpdateCheckoutFulfillment';
import { useServiceProgramInfo } from '../../../../hooks/useServiceProgramInfo';

// ---------------------------------------------------------------------------
// Per-box schemas
// ---------------------------------------------------------------------------
// Each gray box is its own Form so that Save/Clear buttons appear inside
// the box (aligned with the paradigm used on Business Details, Bank Account,
// and AI BDC settings pages).
//
// The backend endpoint (POST /program/checkout/fulfillment) expects the full
// fulfillment payload, so each sub-form merges its changed fields with the
// current SP data before submitting. This is safe because the backend uses
// Firestore dot-notation merges (buildFieldPathUpdates) — only the changed
// leaf fields are actually written.
// ---------------------------------------------------------------------------

const deliverySchema = z.object({ enabled: z.boolean() });
const pickupSchema = z.object({ enabled: z.boolean() });
const autoAssignSchema = z.object({ autoAssign: z.boolean() });

/**
 * Read the current fulfillment config from the services provider.
 */
function getCurrentFulfillment(
	servicesProvider: ServicesProvider | undefined | null,
): ProgramOwnerCheckoutFulfillmentDto {
	const programOwnerConfig = servicesProvider?.config?.[Services.PROGRAM_OWNER] as
		| ProgramOwnerServiceConfig
		| undefined;
	const fulfillment = programOwnerConfig?.checkout?.fulfillment;

	return {
		delivery: { enabled: fulfillment?.delivery?.enabled ?? false },
		pickup: {
			enabled: fulfillment?.pickup?.enabled ?? false,
			autoAssign: fulfillment?.pickup?.autoAssign ?? false,
		},
	};
}

export function CheckoutFulfillmentForm(): React.ReactNode {
	const { data: servicesProvider } = useCurrentServicesProvider();
	const updateCheckoutFulfillment = useUpdateCheckoutFulfillment();
	const programInfo = useServiceProgramInfo(
		PROGRAM_SECTIONS.CHECKOUT,
		SUBSECTIONS.CHECKOUT_FULFILLMENT,
	);

	if (!programInfo.shouldRender) {
		return null;
	}

	const current = getCurrentFulfillment(servicesProvider);

	// Each handler merges its fields with the rest of the current config
	const handleDeliverySubmit = async (
		data: z.infer<typeof deliverySchema>,
	): Promise<{ isSuccess: boolean }> => {
		try {
			await updateCheckoutFulfillment.mutateAsync({
				delivery: data,
				pickup: current.pickup,
			});
			return { isSuccess: true };
		} catch (error) {
			createLog(
				'config-mgmt',
				`Failed to update delivery config: ${error instanceof Error ? error.message : String(error)}`,
				LogSeverity.ERROR,
			);
			return { isSuccess: false };
		}
	};

	const handlePickupSubmit = async (
		data: z.infer<typeof pickupSchema>,
	): Promise<{ isSuccess: boolean }> => {
		try {
			await updateCheckoutFulfillment.mutateAsync({
				delivery: current.delivery,
				pickup: { ...current.pickup, enabled: data.enabled },
			});
			return { isSuccess: true };
		} catch (error) {
			createLog(
				'config-mgmt',
				`Failed to update pickup config: ${error instanceof Error ? error.message : String(error)}`,
				LogSeverity.ERROR,
			);
			return { isSuccess: false };
		}
	};

	const handleAutoAssignSubmit = async (
		data: z.infer<typeof autoAssignSchema>,
	): Promise<{ isSuccess: boolean }> => {
		try {
			await updateCheckoutFulfillment.mutateAsync({
				delivery: current.delivery,
				pickup: { ...current.pickup, autoAssign: data.autoAssign },
			});
			return { isSuccess: true };
		} catch (error) {
			createLog(
				'config-mgmt',
				`Failed to update auto-assign config: ${error instanceof Error ? error.message : String(error)}`,
				LogSeverity.ERROR,
			);
			return { isSuccess: false };
		}
	};

	return (
		<SettingsSection
			title='Fulfillment options'
			description='Set the default fulfillment options buyers see during checkout. These can be further customized per vehicle.'>
			<Stack itemsSpacing='16'>
				<Box padding='24' background='secondary' rounding='12'>
					<Form
						name='checkout-fulfillment-delivery'
						schema={deliverySchema}
						defaultValues={{ enabled: current.delivery.enabled }}
						onSubmit={handleDeliverySubmit}
						showSaveAndClear>
						<Form.Field.Switch
							schemaField='enabled'
							labelText='Delivery'
							descriptionText='Allow buyers to have their vehicle delivered to an address they specify. You will need to arrange delivery and keep the buyer updated on the estimated delivery date through the Ekho portal. To configure delivery pricing, go to the Delivery tab in Sales settings.'
							togglePosition='right'
							titleWeight='semibold'
						/>
					</Form>
				</Box>
				<Box padding='24' background='secondary' rounding='12'>
					<Form
						name='checkout-fulfillment-pickup'
						schema={pickupSchema}
						defaultValues={{ enabled: current.pickup.enabled }}
						onSubmit={handlePickupSubmit}
						showSaveAndClear>
						<Form.Field.Switch
							schemaField='enabled'
							labelText='Pickup'
							descriptionText='Allow buyers to select one of your available pickup locations to collect their vehicle. You will be responsible for arranging the exact pickup time directly with the buyer.'
							togglePosition='right'
							titleWeight='semibold'
						/>
					</Form>
				</Box>
				<Box padding='24' background='secondary' rounding='12'>
					<Form
						name='checkout-fulfillment-auto-assign'
						schema={autoAssignSchema}
						defaultValues={{ autoAssign: current.pickup.autoAssign }}
						onSubmit={handleAutoAssignSubmit}
						showSaveAndClear>
						<Form.Field.Switch
							schemaField='autoAssign'
							labelText='Auto-assign pickup location'
							descriptionText='When enabled, buyers who select pickup are automatically assigned the closest location based on their address. When disabled, buyers choose from all available pickup locations on a map.'
							togglePosition='right'
							titleWeight='semibold'
						/>
					</Form>
				</Box>
			</Stack>
		</SettingsSection>
	);
}

export default CheckoutFulfillmentForm;
